import React, { createContext, useContext, useEffect, useState } from 'react';
import { styled, useMediaQuery } from '@mui/material';
import theme from 'theme';
import { Colors } from 'library';

import { RDropdownMenuItem } from 'recruit/components';

import { LayoutTopBar } from './LayoutTopBar';
import { LayoutSidebar, SIDEBAR_WIDTH, NavItem, AppMode } from './LayoutSidebar';
import { Banner, BannerConfig } from './Banner';

const BannerSlotContext = createContext<HTMLDivElement | null>(null);

export function useBannerSlot() {
    return useContext(BannerSlotContext);
}

interface ModeToggleConfig {
    mode: AppMode;
    onModeChange: (mode: AppMode) => void;
}

interface LayoutProps {
    children: React.ReactNode;
    navItems: NavItem[];
    bottomNavItems?: NavItem[];
    settingsNavItems?: NavItem[];
    modeToggle?: ModeToggleConfig;
    extraBottomContent?: React.ReactNode;
    bannerConfig?: BannerConfig;
    showBottomSection?: boolean;
    initials?: string;
    avatarImageUrl?: string | null;
    accountMenuItems?: RDropdownMenuItem[];
    showNotificationBell?: boolean;
}

export function Layout({
    children,
    navItems,
    bottomNavItems,
    settingsNavItems,
    modeToggle,
    extraBottomContent,
    bannerConfig,
    showBottomSection = true,
    initials,
    avatarImageUrl,
    accountMenuItems,
    showNotificationBell,
}: LayoutProps) {
    const small = useMediaQuery(theme.breakpoints.down('md'));
    const [sidebarOpen, setSidebarOpen] = useState(!small);
    const [bannerSlot, setBannerSlot] = useState<HTMLDivElement | null>(null);

    useEffect(() => {
        setSidebarOpen(!small);
    }, [small]);

    return (
        <BannerSlotContext.Provider value={bannerSlot}>
            <Root>
                <LayoutTopBar
                    small={small}
                    onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
                    initials={initials ?? 'MM'}
                    avatarImageUrl={avatarImageUrl}
                    accountMenuItems={accountMenuItems}
                    showNotificationBell={showNotificationBell}
                />
                <LayoutSidebar
                    small={small}
                    open={sidebarOpen}
                    onClose={() => setSidebarOpen(false)}
                    navItems={navItems}
                    bottomNavItems={bottomNavItems}
                    settingsNavItems={settingsNavItems}
                    modeToggle={modeToggle}
                    extraBottomContent={extraBottomContent}
                    showBottomSection={showBottomSection}
                />
                <Main sidebarOpen={sidebarOpen} small={small}>
                    <div ref={setBannerSlot} />
                    {bannerConfig && (
                        <Banner variant={bannerConfig.variant} badge={bannerConfig.badge} action={bannerConfig.action}>
                            {bannerConfig.message}
                        </Banner>
                    )}
                    <Content>{children}</Content>
                </Main>
            </Root>
        </BannerSlotContext.Provider>
    );
}

const Root = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    backgroundColor: Colors.white,
});

const Main = styled('main')<{ sidebarOpen: boolean; small: boolean }>(({ sidebarOpen, small }) => ({
    flexGrow: 1,
    marginLeft: sidebarOpen && !small ? SIDEBAR_WIDTH : 0,
    marginTop: small ? 50 : 60,
    transition: 'margin-left 0.2s ease',
    padding: small ? 12 : 24,
    backgroundColor: Colors.faintGrey,
    minHeight: 'calc(100vh - 60px)',
}));

const Content = styled('div')({
    width: '100%',
    maxWidth: 1400,
    marginLeft: 'auto',
    marginRight: 'auto',
});
